import type { ReactNode } from 'react';
import type { ChatController } from 'flare-chat-core/controller';
import type { HostConfiguration, HostOptions } from './contracts';
import { ApplicationProvider } from '../application/Provider';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createChatController } from 'flare-chat-core/controller';
import { ChatCoreProvider } from 'flare-chat-core/react';
import { createSessionMessageAPI } from 'flare-chat-core/transport/session-message/api';
import { useRealApiReadinessGate } from './config/connection.ts';
import { useWorkspaceLocationSelection } from '../application/projects/workspace-location.js';
import { createHostCapabilities, hostConfiguration } from './configuration.ts';
import { createSessionScopeTransport } from '../adapters/chat/session-scope-transport.js';
import { ControllerBoundary } from './ControllerBoundary';

/** This owner is above every surface. UI changes do not recreate its Controller. */
export const HostChatProvider = ({ options, children }: { options: HostOptions; children?: ReactNode }) => {
  const workspaceLocation = useWorkspaceLocationSelection(options);
  const projectId = workspaceLocation.effectiveProjectId;
  const { apiBaseUrl, apiToken, fetchImpl, userId, instanceId, domainPackDomain, domainPackVersion,
    functionType, attachmentPolicy, sourcePolling, operationDefinitions, sessionAPI, messageAPI, projectAPI, capabilities } = options;
  const scopeKey = JSON.stringify([JSON.stringify([userId, instanceId, domainPackDomain, domainPackVersion]), projectId]);
  const configuration = useMemo(() => hostConfiguration({ apiBaseUrl, apiToken, fetchImpl, userId,
    instanceId, domainPackDomain, domainPackVersion, functionType, attachmentPolicy, sourcePolling,
  }), [apiBaseUrl, apiToken, fetchImpl, userId, instanceId, domainPackDomain,
    domainPackVersion, functionType, attachmentPolicy, sourcePolling]);
  const apiReadinessGate = useRealApiReadinessGate({
    enabled: options.backendMode === 'real' && !projectAPI && !sessionAPI && !messageAPI,
    apiBaseUrl: configuration.requestOptions.baseUrl,
  });
  const api = useMemo(() => ({ ...createSessionMessageAPI(configuration.requestOptions),
    ...(projectAPI ? { projectAPI } : {}), ...(sessionAPI ? { sessionAPI } : {}), ...(messageAPI ? { messageAPI } : {}),
  }), [configuration.requestOptions, projectAPI, sessionAPI, messageAPI]);
  const [runtime, setRuntime] = useState<{ controller: ChatController; configuration: HostConfiguration; binding: ReturnType<typeof createSessionScopeTransport> } | null>(null);
  useEffect(() => {
    const binding = createSessionScopeTransport({ sessionAPI, messageAPI, operationDefinitions }, configuration);
    const { transport } = binding;
    const controller = createChatController({ transport, capabilities: createHostCapabilities(transport, capabilities), ...configuration.controller });
    setRuntime({ controller, configuration, binding });
    return () => controller.dispose();
  }, [capabilities, configuration, sessionAPI, messageAPI, operationDefinitions]);
  useLayoutEffect(() => {
    if (!runtime || runtime.configuration !== configuration || apiReadinessGate.blocked) return;
    const { lifecycle, sessions } = runtime.controller.getSnapshot();
    if (sessions.scopeKey === scopeKey) return;
    runtime.binding.bindScope(projectId);
    runtime.controller.bindScope(scopeKey);
    // Core initialize is idempotent when ready; a newly bound list needs its public read command.
    const readScope = new Map([['ready', runtime.controller.actions.refreshSessionList]])
      .get(lifecycle.status) ?? runtime.controller.initialize;
    void readScope();
  }, [apiReadinessGate.blocked, configuration, projectId, runtime, scopeKey]);
  if (!runtime || runtime.configuration !== configuration) return null;
  return <ChatCoreProvider controller={runtime.controller}>
    <ApplicationProvider options={options} api={api} workspaceLocation={workspaceLocation} apiReadinessGate={apiReadinessGate}>
      <ControllerBoundary controller={runtime.controller}>{children}</ControllerBoundary>
    </ApplicationProvider>
  </ChatCoreProvider>;
};
